import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import PowerBi from "@pages/PowerBi";
import { PageHeader } from "@features/shared";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import _ from "lodash";
import { getDealerApi } from "../../services/MarketWorking/MyTeam/getDealerApi";

const territoryAutoCompletePlaceHolder = {
  attributes: null,
  Id: null,
  SH2_Name__c: null,
  SH2_Code__c: null,
  Name: null,
  SH3_Code__c: null,
};

const dealerPlaceHolder = {
  attributes: null,
  Id: null,
  Dealer_Name__c: null,
  DealerClass__c: null,
  DealerType1__c: null,
  DealerLatitude__c: null,
  DealerLongitude__c: null,
  PrimaryTSICode__c: null,
  DealerCode__c: null,
  RecordTypeId: null,
};

const PILSimilarSurvey = () => {
  const [selectedTerritory, setSelectedTerritory] = useState(
    territoryAutoCompletePlaceHolder
  );
  const [selectedDealer, setSelectedDealer] = useState(dealerPlaceHolder);
  const [dealerOption, setDealerOption] = useState([]);
  const [territoryPickList, setTerritoryPickList] = useState([]);
  const [error, setError] = useState("");
  const [dealerCode, setDealerCode] = useState("");

  const tname = useSelector((state) => state?.territory?.territoryDetails);

  // Auto-populate territory from tour plan (similar to My Dealer Page)
  useEffect(() => {
    if (!_.isEmpty(tname)) {
      const todayTerritory = tname?.find(
        (item) =>
          new Date(item?.Tour_Date__c).toLocaleDateString("en-CA", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
          }) ===
          new Date().toLocaleDateString("en-CA", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
          })
      );

      if (todayTerritory) {
        setSelectedTerritory(todayTerritory);
        setTerritoryPickList(tname);
      } else if (tname.length > 0) {
        setTerritoryPickList(tname);
      }
    }
  }, [tname]);

  // Fetch dealers when territory is selected
  useEffect(() => {
    // Reset dealers and dealer code when territory changes
    setDealerOption([]);
    setSelectedDealer(dealerPlaceHolder);
    setDealerCode("");

    if (selectedTerritory?.SH2_Code__c !== null) {
      (async () => {
        let SH2_Code__c = selectedTerritory?.SH2_Code__c;
        const response = await getDealerApi(SH2_Code__c);
        if (response?.success) {
          if (response?.data?.length === 0) {
            setError("No dealers found for this territory");
            setDealerOption([]);
          } else {
            setDealerOption(response?.data?.Dealer);
            setError("");
          }
        } else {
          setError(response?.msg);
          setDealerOption([]);
        }
      })();
    }
  }, [selectedTerritory]);

  // Update dealer code when dealer is selected
  useEffect(() => {
    if (selectedDealer?.DealerCode__c) {
      setDealerCode(selectedDealer?.DealerCode__c);
    } else {
      setDealerCode("");
    }
  }, [selectedDealer]);

  const handleTerritoryChange = (event, value) => {
    setSelectedTerritory(value || territoryAutoCompletePlaceHolder);
    setSelectedDealer(dealerPlaceHolder);
    setDealerCode("");
  };

  const handleDealerChange = (event, value) => {
    setSelectedDealer(value || dealerPlaceHolder);
  };

  return (
    <>
      <PageHeader title="PIL Similar Survey Report" path="/" />
      <div className="container p-0">
        <div className="p-4 bg-[#f5f5f5] min-h-screen">
          <div className="flex flex-col md:flex-row flex-wrap gap-4 mb-6 bg-white p-6 rounded-lg shadow-md">
            {/* Territory Dropdown */}
            <div className="flex-1 min-w-full md:min-w-[250px]">
              <Autocomplete
                value={selectedTerritory}
                onChange={handleTerritoryChange}
                options={territoryPickList}
                getOptionLabel={(option) =>
                  option?.SH2_Name__c
                    ? `${option.SH2_Name__c} - ${option.SH2_Code__c}`
                    : ""
                }
                isOptionEqualToValue={(option, value) =>
                  option?.Id === value?.Id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Territory (TTY)"
                    placeholder="Select Territory"
                    variant="outlined"
                  />
                )}
                className="w-full"
              />
            </div>

            {/* Dealer Dropdown */}
            <div className="flex-1 min-w-full md:min-w-[250px]">
              <Autocomplete
                value={selectedDealer}
                onChange={handleDealerChange}
                options={dealerOption}
                getOptionLabel={(option) =>
                  option?.Dealer_Name__c
                    ? `${option.Dealer_Name__c} - ${option.DealerCode__c}`
                    : ""
                }
                isOptionEqualToValue={(option, value) =>
                  option?.DealerCode__c === value?.DealerCode__c
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Dealer"
                    placeholder="Select Dealer"
                    variant="outlined"
                  />
                )}
                disabled={!selectedTerritory?.SH2_Code__c}
                className="w-full"
              />
            </div>
          </div>

          {error && (
            <div className="text-[#d32f2f] bg-[#ffebee] p-3 rounded mb-4 text-[0.9rem] border-l-4 border-[#d32f2f]">
              {error}
            </div>
          )}

          {/* Power BI Dashboard */}
          <div className="">
            <PowerBi
              key={`pil-survey-${dealerCode || "no-dealer"}`}
              id="1e8069b9-85d5-416c-ab1c-d7583a3a7e48"
              pageName={{
                id: "b8ed9801a66e9699a458",
                name: "PIL Similar Survey Report",
              }}
              dealerCode={dealerCode}
              filterName="DimHierarchy"
              height="70vh"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PILSimilarSurvey;
